import React from 'react';

import { tv } from '../../tv';
import { Size } from '../../types';

/** Set by PropertyList and Property: the row's name, and whether the list
 * can be edited at all. Shared by direct import, never through the barrel. */
export const PropertyContext = React.createContext<{
  label?: string;
  isReadOnly: boolean;
  /** Inside a PropertyList, whose column gap holds a value box's start bleed. */
  isInList: boolean;
}>({ isReadOnly: false, isInList: false });

/**
 * One box for a value, read or edited: the field frame's geometry per step
 * (the value-box utilities), a 1px border that is transparent while reading,
 * and a start bleed that puts the read text on the column edge. Shared with
 * InlineEdit, which swaps the content and keeps the box. A grid, so an
 * editor and the invisible copy of its text that sizes it share one cell.
 */
export const valueBoxStyles = tv({
  slots: {
    box: [
      'ck:relative ck:grid ck:rounded-3 ck:border ck:border-transparent',
      'ck:font-sans ck:text-text-default ck:transition-frame',
    ],
    text: 'ck:col-start-1 ck:row-start-1 ck:min-w-0 ck:whitespace-pre-wrap ck:break-words',
  },
  variants: {
    step: {
      sm: { box: 'ck:value-box-sm ck:text-xs' },
      md: { box: 'ck:value-box-md ck:text-sm' },
      lg: { box: 'ck:value-box-lg ck:text-base' },
      /* The entity name heading a drawer: bold, as the product sets it. */
      title: { box: 'ck:value-box-title ck:text-xl ck:font-bold' },
    },
    state: {
      static: {},
      read: { box: 'ck:hover:bg-neutral-a-3' },
      editing: {
        box: 'ck:border-border-active ck:entry-focus:border-accent-8 ck:entry-focus:focus-halo ck:dark:bg-neutral-a-2 ck:entry-focus-visible:focus-ring',
      },
      invalid: {
        box: 'ck:border-error-border ck:entry-focus:focus-halo-error ck:dark:bg-neutral-a-2 ck:entry-focus-visible:focus-ring',
      },
    },
    isEmpty: {
      true: { text: 'ck:text-text-muted' },
      false: {},
    },
    /* Pulls the box out by its padding and border, so the read text sits on
     * the column's edge: only where the column holds that room. */
    bleed: { true: {}, false: {} },
  },
  compoundVariants: [
    { step: 'sm', bleed: true, class: { box: 'ck:value-box-bleed-sm' } },
    { step: 'md', bleed: true, class: { box: 'ck:value-box-bleed-md' } },
    { step: 'lg', bleed: true, class: { box: 'ck:value-box-bleed-lg' } },
    { step: 'title', bleed: true, class: { box: 'ck:value-box-bleed-title' } },
  ],
  defaultVariants: { bleed: false },
});

const listStyles = tv({
  slots: {
    /* Baseline alignment puts each term on its value's first line, whatever
     * the value's step. The 24px column gap is what the value's start bleed
     * takes back, leaving the hover wash clear of the term. */
    list: 'ck:m-0 ck:grid ck:grid-cols-label ck:items-baseline ck:gap-x-6 ck:gap-y-1 ck:font-sans',
    row: 'ck:contents',
    term: 'ck:min-w-0 ck:truncate ck:text-xs ck:font-medium ck:text-text-muted',
    detail: 'ck:m-0 ck:min-w-0',
  },
});

export interface PropertyListProps {
  /** The rows: one Property each. */
  children: React.ReactNode;
  /**
   * Nothing in the list can be edited: every InlineEdit inside renders as
   * plain text. Say why once, near the list, rather than on every row.
   */
  isReadOnly?: boolean;
}

/**
 * The details-tab layout: a column of named values, each term beside its
 * value (field.md, "Property lists"). A description list, so the pairs are
 * announced as terms and details. The values are what the rows hold:
 * PropertyValue for text nobody edits here, InlineEdit for free text edited
 * in place.
 */
export const PropertyList = React.forwardRef<HTMLDListElement, PropertyListProps>(
  function PropertyList({ children, isReadOnly = false }, ref) {
    const { list } = listStyles();
    const value = React.useMemo(() => ({ isReadOnly, isInList: true }), [isReadOnly]);
    return (
      <PropertyContext.Provider value={value}>
        <dl ref={ref} className={list()} data-component="property-list">
          {children}
        </dl>
      </PropertyContext.Provider>
    );
  },
);

export interface PropertyProps {
  /** The term: names the value, visibly and for the editor inside. */
  label: string;
  /** The value: a PropertyValue or an InlineEdit. */
  children: React.ReactNode;
}

/** One row of a PropertyList: the term, then its value. */
export const Property = React.forwardRef<HTMLDivElement, PropertyProps>(function Property(
  { label, children },
  ref,
) {
  const list = React.useContext(PropertyContext);
  const { row, term, detail } = listStyles();
  const value = React.useMemo(() => ({ ...list, label }), [list, label]);
  return (
    <PropertyContext.Provider value={value}>
      {/* A wrapper is valid inside a dl and dissolves into the grid. */}
      <div ref={ref} className={row()} data-component="property">
        <dt className={term()}>{label}</dt>
        <dd className={detail()}>{children}</dd>
      </div>
    </PropertyContext.Provider>
  );
});

export interface PropertyValueProps {
  /** The value as text or inline content. */
  children?: React.ReactNode;
  /** Shown muted when there is no value ("No end date"). */
  placeholder?: string;
  /** The text step, matched to the editable values around it. */
  size?: Size;
}

/**
 * A value nobody edits here, as plain text, never a disabled input. It sits
 * in the same box as an editable value, so a list of both stays aligned.
 */
export const PropertyValue = React.forwardRef<HTMLDivElement, PropertyValueProps>(
  function PropertyValue({ children, placeholder, size = 'md' }, ref) {
    const isEmpty = children === undefined || children === null || children === '';
    const { box, text } = valueBoxStyles({ step: size, state: 'static', isEmpty, bleed: true });
    return (
      <div ref={ref} className={box()} data-component="property-value">
        <span className={text()}>{isEmpty ? placeholder : children}</span>
      </div>
    );
  },
);
